import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { PDFDocument } from 'pdf-lib';
import {
  ArrowUpTrayIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

export default function PublicPostFilingDocumentsUpload() {
  const { tokenId } = useParams();
  const apiBase = useMemo(() => (process.env.REACT_APP_API_URL || '').replace(/\/$/, ''), []);
  const endpoint = `${apiBase}/api/public/post-filing-documents/${tokenId}/`;
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState({});
  const [fileInputKeys, setFileInputKeys] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [messages, setMessages] = useState({});

  const load = async () => {
    setLoading(true); setPageError('');
    try {
      const response = await fetch(endpoint);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'This upload link is invalid or expired.');
      setInfo(data);
    } catch (error) {
      setPageError(error.message || 'The requested documents could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const uniqueId = () => (
    window.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );

  const safeBaseName = (value) => (
    String(value || 'requested-document')
      .split('?')[0]
      .replace(/^https?:\/\//, '')
      .replace(/[^a-z0-9_-]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 30)
    || 'requested-document'
  );

  const enhanceImage = async (file) => {
    const bitmap = await createImageBitmap(file);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const context = canvas.getContext('2d');
      context.drawImage(bitmap, 0, 0);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      for (let index = 0; index < pixels.length; index += 4) {
        const gray = 0.299 * pixels[index] + 0.587 * pixels[index + 1] + 0.114 * pixels[index + 2];
        const contrasted = (gray - 128) * 1.5 + 128;
        const finalValue = Math.max(0, Math.min(255, contrasted));
        pixels[index] = finalValue;
        pixels[index + 1] = finalValue;
        pixels[index + 2] = finalValue;
      }
      context.putImageData(imageData, 0, 0);
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (result) => result ? resolve(result) : reject(new Error('The image could not be prepared.')),
          'image/jpeg',
          0.5,
        );
      });
      return new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
    } finally {
      bitmap.close?.();
    }
  };

  const createMultiPagePDF = async (images, itemName) => {
    const pageWidth = 612;
    const pageHeight = 792;
    const maxImageSize = 900;
    const fiveMegabytes = 5 * 1024 * 1024;

    const buildPdf = async (quality) => {
      const pdf = new jsPDF({ unit: 'pt', format: 'letter' });
      for (let index = 0; index < images.length; index += 1) {
        const bitmap = await createImageBitmap(images[index]);
        try {
          const scale = Math.min(1, maxImageSize / Math.max(bitmap.width, bitmap.height));
          const imageWidth = Math.max(1, Math.floor(bitmap.width * scale));
          const imageHeight = Math.max(1, Math.floor(bitmap.height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = imageWidth;
          canvas.height = imageHeight;
          const context = canvas.getContext('2d');
          context.drawImage(bitmap, 0, 0, imageWidth, imageHeight);
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          const pdfScale = Math.min(pageWidth / imageWidth, pageHeight / imageHeight);
          const pdfWidth = imageWidth * pdfScale;
          const pdfHeight = imageHeight * pdfScale;
          if (index > 0) pdf.addPage();
          pdf.addImage(
            dataUrl,
            'JPEG',
            (pageWidth - pdfWidth) / 2,
            (pageHeight - pdfHeight) / 2,
            pdfWidth,
            pdfHeight,
          );
        } finally {
          bitmap.close?.();
        }
      }
      return pdf.output('blob');
    };

    let blob = await buildPdf(1);
    if (blob.size > fiveMegabytes) blob = await buildPdf(0.4);
    return new File(
      [blob],
      `${safeBaseName(itemName)}-${uniqueId()}.pdf`,
      { type: 'application/pdf' },
    );
  };

  const mergePDFs = async (files, itemName) => {
    const merged = await PDFDocument.create();
    for (const file of files) {
      const source = await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: false });
      const pages = await merged.copyPages(source, source.getPageIndices());
      pages.forEach((page) => merged.addPage(page));
    }
    return new File(
      [await merged.save()],
      `${safeBaseName(itemName)}-${uniqueId()}.pdf`,
      { type: 'application/pdf' },
    );
  };

  const prepareUploadPDFs = async (files, itemName) => {
    const imageExtensions = /\.(jpe?g|png|heic|heif|tiff?|webp)$/i;
    const images = files.filter((file) => (
      file.type?.startsWith('image/') || imageExtensions.test(file.name || '')
    ));
    const pdfs = files.filter((file) => (
      file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf')
    ));
    if (images.length + pdfs.length !== files.length) {
      throw new Error('Only PDF files and common image formats may be uploaded.');
    }

    const prepared = [];
    if (images.length) {
      try {
        const skipEnhancement = /photo id|social security/i.test(itemName);
        const processed = skipEnhancement
          ? images
          : await Promise.all(images.map(enhanceImage));
        prepared.push(await createMultiPagePDF(processed, itemName));
      } catch (error) {
        throw new Error(
          'One of the selected pictures could not be converted to PDF. '
          + 'Try taking a new picture or upload the document as a PDF.',
        );
      }
    }

    if (pdfs.length) {
      const ordinary = [];
      const encrypted = [];
      for (const file of pdfs) {
        try {
          await PDFDocument.load(await file.arrayBuffer(), { ignoreEncryption: false });
          ordinary.push(file);
        } catch (error) {
          encrypted.push(file);
        }
      }
      if (ordinary.length) prepared.push(await mergePDFs(ordinary, itemName));
      encrypted.forEach((file) => prepared.push(new File(
        [file],
        `${safeBaseName(itemName)}-${uniqueId()}.pdf`,
        { type: 'application/pdf' },
      )));
    }
    return prepared;
  };

  const upload = async (item) => {
    const files = selectedFiles[item.id] || [];
    if (!files.length) {
      setMessages((current) => ({ ...current, [item.id]: { ok: false, text: 'Choose at least one file first.' } }));
      return;
    }
    setUploadingId(item.id);
    setMessages((current) => ({ ...current, [item.id]: null }));
    try {
      const preparedFiles = await prepareUploadPDFs(files, item.name);
      const formData = new FormData();
      formData.append('checklist_item_id', item.id);
      preparedFiles.forEach((file) => formData.append('files', file));
      const response = await fetch(endpoint, { method: 'POST', body: formData });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'The upload failed. Please try again.');
      setMessages((current) => ({ ...current, [item.id]: { ok: true, text: data.detail || 'Upload received.' } }));
      setSelectedFiles((current) => ({ ...current, [item.id]: [] }));
      setFileInputKeys((current) => ({
        ...current,
        [item.id]: (current[item.id] || 0) + 1,
      }));
      setInfo((current) => ({
        ...current,
        items: current.items.map((entry) => entry.id === item.id ? { ...entry, received: true } : entry),
      }));
    } catch (error) {
      setMessages((current) => ({ ...current, [item.id]: { ok: false, text: error.message } }));
    } finally {
      setUploadingId(null);
    }
  };

  const outstanding = info?.items?.filter((item) => !item.received) || [];

  return (
    <main className="min-h-screen bg-slate-100 px-4 py-10 text-slate-900 sm:px-6">
      <div className="mx-auto max-w-2xl">
        <header className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-sm">
            <ArrowUpTrayIcon className="h-8 w-8" />
          </div>
          <h1 className="mt-4 text-3xl font-bold">Upload Requested Documents</h1>
          <p className="mt-2 text-slate-600">Secure document upload for {info?.firm_name || 'your bankruptcy attorney'}</p>
        </header>

        {loading ? (
          <div className="mt-8 rounded-xl bg-white p-8 text-center shadow-sm">Loading your requested documents…</div>
        ) : pageError ? (
          <div className="mt-8 rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
            <ExclamationTriangleIcon className="h-7 w-7" />
            <p className="mt-2 font-semibold">{pageError}</p>
            <p className="mt-2 text-sm">Please contact the law office for a new upload link.</p>
          </div>
        ) : (
          <>
            <section className="mt-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-sm text-slate-600"><strong className="text-slate-900">Client:</strong> {info.debtor_names || 'Debtor'}</p>
              <p className="mt-1 text-sm text-slate-600"><strong className="text-slate-900">Case:</strong> {info.case_reference}</p>
              {info.meeting_display && <p className="mt-1 text-sm text-slate-600"><strong className="text-slate-900">341 meeting:</strong> {info.meeting_display}</p>}
              <p className="mt-4 text-sm leading-6 text-slate-600">Upload each requested document under its matching name. You may select PDFs or clear photographs and may select multiple pages at once. Pictures are combined and converted into a PDF before they are uploaded.</p>
            </section>

            {outstanding.length === 0 && (
              <section className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center text-emerald-800">
                <CheckCircleIcon className="mx-auto h-10 w-10" />
                <h2 className="mt-2 text-lg font-bold">Everything requested has been uploaded</h2>
                <p className="mt-1 text-sm">Thank you. The firm will review your documents. You may still add more pages or files below.</p>
              </section>
            )}

            <div className="mt-5 space-y-4">
              {info.items.map((item) => {
                const message = messages[item.id];
                const files = selectedFiles[item.id] || [];
                return (
                  <section key={item.id} className={`rounded-xl border bg-white p-5 shadow-sm ${item.received ? 'border-emerald-200' : 'border-slate-200'}`}>
                    <div className="flex items-start gap-3">
                      {item.received ? <CheckCircleIcon className="h-6 w-6 shrink-0 text-emerald-600" /> : <DocumentTextIcon className="h-6 w-6 shrink-0 text-indigo-600" />}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h2 className="font-bold text-slate-900">{item.name}</h2>
                          {item.received && <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">Received</span>}
                        </div>
                        {item.description && <p className="mt-1 text-sm text-slate-600">{item.description}</p>}
                        {item.received && (
                          <p className="mt-1 text-xs font-medium text-emerald-700">
                            Files are already on this item. You can upload additional pages or corrected files below.
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="mb-2 block text-sm font-semibold text-slate-800">
                        {item.received ? 'Add more pictures or files' : 'Choose pictures or files'}
                      </label>
                      <input
                        key={`${item.id}-${fileInputKeys[item.id] || 0}`}
                        type="file"
                        multiple
                        accept="application/pdf,image/*"
                        onChange={(event) => setSelectedFiles((current) => ({
                          ...current,
                          [item.id]: Array.from(event.target.files || []),
                        }))}
                        className="block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:font-semibold file:text-indigo-700 hover:file:bg-indigo-100"
                      />
                      <p className="mt-2 text-xs text-slate-500">
                        Select several pictures or files at once, or submit this group and return here to add another group.
                      </p>
                      {files.length > 0 && <p className="mt-2 text-xs text-slate-500">{files.length} file{files.length === 1 ? '' : 's'} selected: {files.map((file) => file.name).join(', ')}</p>}
                      <button
                        type="button"
                        disabled={uploadingId === item.id || files.length === 0}
                        onClick={() => upload(item)}
                        className="mt-3 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <ArrowUpTrayIcon className="h-5 w-5" />
                        {uploadingId === item.id
                          ? 'Preparing PDF and uploading…'
                          : item.received
                            ? `Add files to ${item.name}`
                            : `Upload ${item.name}`}
                      </button>
                    </div>
                    {message && <p className={`mt-3 rounded-md p-3 text-sm font-medium ${message.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>{message.text}</p>}
                  </section>
                );
              })}
            </div>

            <p className="mt-6 text-center text-xs text-slate-500">This secure link does not require a portal password. Only use it to upload the documents listed above.</p>
          </>
        )}
      </div>
    </main>
  );
}
